import React, { MouseEvent } from "react";
import styled from "@emotion/styled";
import { Flex, Text } from "styles/utility-components";

import Card from "components/ui/card/Card";
import Tag from "components/ui/tag/Tag";
import UIcon, { IconProps } from "lib/icons/untitled_ui/UIcon";
import { DataTypeName } from "lib/types/models";

type CardSize = "sm" | "lg";
export type PartType = "Strategy" | "Pipeline" | "Tenders" | "Bids";
type Content = {
  title: string;
  description: {
    sm: string;
    lg: string;
  };
  icon: IconProps["icon"];
  requiresDataType?: DataTypeName;
  showNewTag?: boolean;
};

function getCardContent(partType: PartType): Content {
  const cardContent: Record<PartType, Content> = {
    Strategy: {
      title: "Create strategy",
      description: {
        sm: "Find top buyers and suppliers within your market.",
        lg: "Analyse your market, find the right buyers to sell to and identify potential competitors or partners suppliers.",
      },
      icon: "strategyPart",
      showNewTag: true,
    },
    Pipeline: {
      title: "Build pipeline",
      description: {
        sm: "Find upcoming contracts to pre-engage.",
        lg: "Find early buying signals, build your coverage and outreach to decision makers in minutes.",
      },
      icon: "pipelinePart",
    },
    Tenders: {
      title: "Track tenders",
      description: {
        sm: "Find the best open tenders from every portal on the market.",
        lg: "Build a centralised view of open tenders pull from every portal on the market.",
      },
      icon: "tenderPart",
    },
    Bids: {
      title: "Win bids",
      description: {
        sm: "Generate a bid / no-bid report for an open tender.",
        lg: "Stream line your bid process, leveraging AI to qualify, collaborate and draft faster and more effectively.",
      },
      icon: "bidPart",
      showNewTag: true,
    },
  };

  return cardContent[partType];
}

type Props = {
  partType: PartType;
  hasPaywall?: boolean;
  onClick?: (e: MouseEvent) => void;
  size?: CardSize;
  href?: string;
};

export default function PartCard({
  partType,
  hasPaywall,
  onClick,
  href,
  size = "sm",
}: Props): JSX.Element {
  const { title, description, icon, showNewTag } = getCardContent(partType);
  if (size === "lg") {
    return (
      <Card width={448} height={105} onClick={onClick} href={href}>
        <Flex justifyContent="space-between" alignItems="center" gap={16} height="100%">
          <UIcon icon={icon} size={40} />
          <Flex column gap={8} flexGrow={1}>
            <Title>
              {title}
              {hasPaywall && <UIcon icon={"lock01"} size={14} />}
            </Title>
            <Text description>{description[size]}</Text>
          </Flex>
        </Flex>
      </Card>
    );
  }

  return (
    <Card onClick={onClick} href={href}>
      <Title>
        <UIcon icon={icon} size={14} />
        {title}
        {showNewTag && <Tag label="NEW" variant="secondary" fontSize="sm" />}
        {hasPaywall && <UIcon icon={"lock01"} size={14} />}
      </Title>
      <Text>{description[size]}</Text>
    </Card>
  );
}

const Title = styled(Flex)(({ theme }) => ({
  alignItems: "center",
  fontWeight: 600,
  gap: "8px",
  color: theme.colors.sysTextDefault,
}));
